import { type ReactNode, useState } from "react";
import { FileDown, Save, Trash2 } from "lucide-react";
import type { PdfGenerationResponseDto } from "../../api/schemas";
import {
  calculateAmount,
  calculateDiscountedRate,
  calculateFinalRate,
  calculateGst,
  calculateSubtotal,
  roundMoney,
  pricingExplanation
} from "../../domain/pricing";
import type { CartItem, ComparisonRow, WorkspaceState } from "../../domain/quotation";
import { NumberField } from "../common/NumberField";

interface QuotationCartProps {
  state: WorkspaceState;
  children?: ReactNode;
  recentlyAddedItemId?: string;
  onPriceBreakdownChange: (enabled: boolean) => void;
  onDeliveryChargesChange: (deliveryCharges: number | undefined) => void;
  comparisonBrand: string;
  comparisonBrandOptions: string[];
  isGenerating: boolean;
  isSaving: boolean;
  result: PdfGenerationResponseDto | undefined;
  lastPdfUrl: string | undefined;
  lastPdfNumber: string | undefined;
  errorMessage: string | undefined;
  saveErrorMessage: string | undefined;
  onQuantityChange: (id: string, quantity: number | undefined) => void;
  onDiscountChange: (id: string, discount: number | undefined) => void;
  onRateChange: (id: string, rate: number | undefined) => void;
  onMarkupChange: (id: string, markup: number | undefined) => void;
  onFrlsChange: (id: string, enabled: boolean) => void;
  onPvcChange: (id: string, enabled: boolean) => void;
  onComparisonBrandChange: (brand: string) => void;
  onComparisonDiscountChange: (item: CartItem, discount: number | undefined) => void;
  onComparisonRateChange: (id: string, rate: number | undefined) => void;
  onRemove: (id: string) => void;
  onSave: () => void;
  onGenerate: () => void;
}

export function QuotationCart({
  state,
  children,
  recentlyAddedItemId,
  onPriceBreakdownChange,
  onDeliveryChargesChange,
  comparisonBrand,
  comparisonBrandOptions,
  isGenerating,
  isSaving,
  result,
  lastPdfUrl,
  lastPdfNumber,
  errorMessage,
  saveErrorMessage,
  onQuantityChange,
  onDiscountChange,
  onRateChange,
  onMarkupChange,
  onFrlsChange,
  onPvcChange,
  onComparisonBrandChange,
  onComparisonDiscountChange,
  onComparisonRateChange,
  onRemove,
  onSave,
  onGenerate
}: QuotationCartProps) {
  const [activeTab, setActiveTab] = useState<"cart" | "comparison">("cart");
  const subtotal = calculateSubtotal(state.cart);
  const totalBeforeGst = roundMoney(subtotal + (state.deliveryCharges ?? 0));
  const gst = calculateGst(totalBeforeGst);
  const grandTotal = Math.round(totalBeforeGst + gst);
  const hasIncompleteRows = state.cart.some(
    (item) => !item.quantity || !item.rate || item.quantity <= 0 || item.rate <= 0
  );

  return (
    <>
      <section className="panel cart-panel" aria-label="Quotation cart">
        <div className="section-heading">
          <div>
            <h2>Items Selected</h2>
            <p
              key={recentlyAddedItemId ?? "idle"}
              className={recentlyAddedItemId ? "cart-count-changed" : undefined}
              aria-live="polite"
              aria-atomic="true"
            >
              {state.cart.length} item{state.cart.length === 1 ? "" : "s"} selected
            </p>
          </div>
        </div>

        <div className="cart-tabs" role="tablist" aria-label="Quote row tabs">
          <button
            type="button"
            className={activeTab === "cart" ? "cart-tab cart-tab-active" : "cart-tab"}
            onClick={() => setActiveTab("cart")}
          >
            Cart
          </button>
          <button
            type="button"
            className={
              activeTab === "comparison" ? "cart-tab cart-tab-active" : "cart-tab"
            }
            onClick={() => setActiveTab("comparison")}
          >
            Comparison
          </button>
        </div>

        {activeTab === "cart" ? (
          <>
            {state.cart.length === 0 ? (
              <p className="muted">Add products from the grid to build a quotation.</p>
            ) : (
              <div className="cart-list">
                {state.cart.map((item) => (
                  <CartRow
                    key={item.id}
                    item={item}
                    recentlyAdded={item.id === recentlyAddedItemId}
                    onQuantityChange={onQuantityChange}
                    onDiscountChange={onDiscountChange}
                    onRateChange={onRateChange}
                    onMarkupChange={onMarkupChange}
                    onFrlsChange={onFrlsChange}
                    onPvcChange={onPvcChange}
                    onRemove={onRemove}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <ComparisonTab
            state={state}
            comparisonBrand={comparisonBrand}
            comparisonBrandOptions={comparisonBrandOptions}
            onComparisonBrandChange={onComparisonBrandChange}
            onComparisonDiscountChange={onComparisonDiscountChange}
            onComparisonRateChange={onComparisonRateChange}
          />
        )}

        {activeTab === "cart" && (
          <div className="cart-pricing-summary">
            <NumberField
              className="delivery-charges-field"
              id="delivery-charges"
              label="Delivery charges"
              value={state.deliveryCharges}
              step={1}
              onChange={onDeliveryChargesChange}
            />
            <dl className="totals">
              <div>
                <dt>Subtotal</dt>
                <dd>Rs {subtotal.toLocaleString("en-IN")}</dd>
              </div>
              <div>
                <dt>Delivery</dt>
                <dd>Rs {(state.deliveryCharges ?? 0).toLocaleString("en-IN")}</dd>
              </div>
              <div>
                <dt>GST</dt>
                <dd>Rs {gst.toLocaleString("en-IN")}</dd>
              </div>
              <div className="grand-total">
                <dt>Total</dt>
                <dd>Rs {grandTotal.toLocaleString("en-IN")}</dd>
              </div>
            </dl>
          </div>
        )}
      </section>

      {children}

      <section className="panel quote-actions-panel" aria-label="Quote actions">

        <label className="checkbox-field pdf-breakdown-option">
          <input
            type="checkbox"
            checked={state.showPriceBreakdown}
            onChange={(event) => onPriceBreakdownChange(event.currentTarget.checked)}
          />
          <span>Show list price and effective discount in PDF</span>
        </label>
        <div className="cart-actions">
          <button
            type="button"
            className="secondary-button"
            disabled={isSaving}
            onClick={onSave}
          >
            <Save aria-hidden="true" size={16} />
            {isSaving ? "Saving" : "Save"}
          </button>
          <button
            type="button"
            className="primary-button"
            disabled={state.cart.length === 0 || hasIncompleteRows || isGenerating || isSaving}
            onClick={onGenerate}
          >
            <FileDown aria-hidden="true" size={18} />
            {isGenerating ? "Generating" : "Generate PDF"}
          </button>
          {(result?.url ?? lastPdfUrl) && (
            <a
              className="pdf-link"
              href={result?.url ?? lastPdfUrl}
              target="_blank"
              rel="noreferrer"
            >
              Open {result?.quotation_number ?? lastPdfNumber ?? "PDF"}
            </a>
          )}
        </div>

        {errorMessage && <p className="error-text">{errorMessage}</p>}
        {saveErrorMessage && <p className="error-text">{saveErrorMessage}</p>}
      </section>
    </>
  );
}

interface ComparisonTabProps {
  state: WorkspaceState;
  comparisonBrand: string;
  comparisonBrandOptions: string[];
  onComparisonBrandChange: (brand: string) => void;
  onComparisonDiscountChange: (item: CartItem, discount: number | undefined) => void;
  onComparisonRateChange: (id: string, rate: number | undefined) => void;
}

function ComparisonTab({
  state,
  comparisonBrand,
  comparisonBrandOptions,
  onComparisonBrandChange,
  onComparisonDiscountChange,
  onComparisonRateChange
}: ComparisonTabProps) {
  const rowsByItemId = new Map(
    state.comparison.rows.map((row) => [row.cartItemId, row])
  );
  const quotedSubtotal = calculateSubtotal(state.cart);
  const comparisonSubtotal = roundMoney(
    state.cart.reduce((total, item) => {
      const row = rowsByItemId.get(item.id);
      return total + comparisonAmount(item, row);
    }, 0)
  );

  if (state.cart.length === 0) {
    return <p className="muted">Add products from the grid to compare prices.</p>;
  }

  return (
    <div className="comparison-panel">
      <label className="field comparison-brand-field" htmlFor="comparison-brand-select">
        <span>Compare With</span>
        <select
          id="comparison-brand-select"
          value={comparisonBrand}
          onChange={(event) => onComparisonBrandChange(event.currentTarget.value)}
          disabled={comparisonBrandOptions.length === 0}
        >
          {comparisonBrandOptions.map((brand) => (
            <option key={brand} value={brand}>
              {brand.toUpperCase()}
            </option>
          ))}
        </select>
      </label>

      {comparisonBrandOptions.length === 0 ? (
        <p className="muted">No alternate brand is available for comparison.</p>
      ) : (
        <>
          <div className="comparison-list">
            {state.cart.map((item) => (
              <ComparisonRowView
                key={item.id}
                item={item}
                row={rowsByItemId.get(item.id)}
                onDiscountChange={onComparisonDiscountChange}
                onRateChange={onComparisonRateChange}
              />
            ))}
          </div>

          <div className="comparison-row comparison-total-row">
            <div className="comparison-total-label">Total</div>
            <div />
            <div />
            <div className="comparison-total-value">
              <strong>Rs {quotedSubtotal.toLocaleString("en-IN")}</strong>
            </div>
            <div />
            <div />
            <div className="comparison-total-value comparison-total-value-right">
              <strong>Rs {comparisonSubtotal.toLocaleString("en-IN")}</strong>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

interface ComparisonRowViewProps {
  item: CartItem;
  row: ComparisonRow | undefined;
  onDiscountChange: (item: CartItem, discount: number | undefined) => void;
  onRateChange: (id: string, rate: number | undefined) => void;
}

function ComparisonRowView({
  item,
  row,
  onDiscountChange,
  onRateChange
}: ComparisonRowViewProps) {
  const quotedRate = calculateFinalRate(item.rate ?? 0, item.markup ?? 0);
  const comparisonRate = comparisonRateForRow(row);
  const amount = comparisonAmount(item, row);
  const canCompare = quotedRate > 0 && comparisonRate > 0 && !row?.warning;
  const quotedIsLower = canCompare && roundMoney(quotedRate) < roundMoney(comparisonRate);
  const comparisonIsLower = canCompare && roundMoney(comparisonRate) < roundMoney(quotedRate);

  return (
    <article className={row?.warning ? "comparison-row comparison-row-warning" : "comparison-row"}>
      <div className="cart-row-title">
        <h3 title={item.description}>{item.description}</h3>
        {row?.warning && <p>{row.warning}</p>}
      </div>
      <div className="comparison-readonly">
        <span>Qty</span>
        <strong>{item.quantity ?? "-"}</strong>
      </div>
      <div className="comparison-readonly">
        <span>Quoted Rate</span>
        <strong className={quotedIsLower ? "lower-price" : undefined}>Rs {quotedRate.toLocaleString("en-IN")}</strong>
      </div>
      <div className="comparison-readonly">
        <span>Quoted Amount</span>
        <strong>Rs {calculateAmount(item).toLocaleString("en-IN")}</strong>
      </div>
      <NumberField
        id={`${item.id}-comparison-discount`}
        label="Discount %"
        value={row?.discount}
        max={100}
        step={0.1}
        onChange={(value) => onDiscountChange(item, value)}
      />
      <NumberField
        className={comparisonIsLower ? "lower-price-field" : undefined}
        id={`${item.id}-comparison-rate`}
        label="Rate"
        value={comparisonRate}
        step={0.01}
        onChange={(value) => onRateChange(item.id, value)}
      />
      <div className="row-money">
        <span>Amount</span>
        <strong>Rs {amount.toLocaleString("en-IN")}</strong>
      </div>
    </article>
  );
}

function comparisonAmount(item: CartItem, row: ComparisonRow | undefined): number {
  return roundMoney((item.quantity ?? 0) * comparisonRateForRow(row));
}

function comparisonRateForRow(row: ComparisonRow | undefined): number {
  if (!row) {
    return 0;
  }
  if (row.pricingMode === "manual") {
    return row.rate ?? 0;
  }
  if (row.listPrice === undefined || row.discount === undefined) {
    return 0;
  }

  // Comparison discount mode caches the competitor list price and prices locally.
  return calculateDiscountedRate(row.listPrice, row.discount);
}

interface CartRowProps {
  item: CartItem;
  recentlyAdded: boolean;
  onQuantityChange: (id: string, quantity: number | undefined) => void;
  onDiscountChange: (id: string, discount: number | undefined) => void;
  onRateChange: (id: string, rate: number | undefined) => void;
  onMarkupChange: (id: string, markup: number | undefined) => void;
  onFrlsChange: (id: string, enabled: boolean) => void;
  onPvcChange: (id: string, enabled: boolean) => void;
  onRemove: (id: string) => void;
}

function CartRow({
  item,
  recentlyAdded,
  onQuantityChange,
  onDiscountChange,
  onRateChange,
  onMarkupChange,
  onFrlsChange,
  onPvcChange,
  onRemove
}: CartRowProps) {
  const finalRate = calculateFinalRate(item.rate ?? 0, item.markup ?? 0);
  const amount = calculateAmount(item);

  return (
    <article className={recentlyAdded ? "cart-row cart-row-added" : "cart-row"} data-pricing-mode={item.pricingMode}>
      <div className="cart-row-title">
        <h3 title={item.description}>{item.description}</h3>
      </div>
      <NumberField
        id={`${item.id}-qty`}
        label="Qty"
        value={item.quantity}
        min={1}
        step={1}
        onChange={(value) => onQuantityChange(item.id, value)}
      />
      <NumberField
        className={item.pricingMode === "manual" ? "pricing-muted" : item.pricingMode ? "pricing-active" : undefined}
        id={`${item.id}-discount`}
        label="Discount %"
        value={item.discount}
        max={100}
        step={0.1}
        onChange={(value) => onDiscountChange(item.id, value)}
      />
      <NumberField
        className={item.pricingMode === "manual" ? "pricing-active" : undefined}
        id={`${item.id}-rate`}
        label="Rate"
        value={item.rate}
        step={0.01}
        onChange={(value) => onRateChange(item.id, value)}
      />
      <NumberField
        className={item.markup ? "pricing-active" : undefined}
        id={`${item.id}-markup`}
        label="Markup %"
        value={item.markup}
        max={100}
        step={0.1}
        onChange={(value) => onMarkupChange(item.id, value)}
      />
      <div className={item.pricingMode === "manual" ? "row-flags pricing-muted" : "row-flags"}>
        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={item.loadingFrls}
            disabled={!item.supportsFrls}
            onChange={(event) => onFrlsChange(item.id, event.currentTarget.checked)}
          />
          <span>FRLS</span>
        </label>
        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={item.loadingPvc}
            disabled={!item.supportsPvc}
            onChange={(event) => onPvcChange(item.id, event.currentTarget.checked)}
          />
          <span>PVC</span>
        </label>
      </div>
      <div className="row-money">
        <span className="rate-explanation" tabIndex={0} aria-describedby={`${item.id}-calculation`}>
          Rate Rs {finalRate.toLocaleString("en-IN")}
          <span className="rate-tooltip" role="tooltip" id={`${item.id}-calculation`}>
            {pricingExplanation(item)}
          </span>
        </span>
        <strong>Rs {amount.toLocaleString("en-IN")}</strong>
      </div>
      <button
        type="button"
        className="icon-button"
        aria-label={`Remove ${item.description}`}
        onClick={() => onRemove(item.id)}
      >
        <Trash2 aria-hidden="true" size={16} />
      </button>
    </article>
  );
}
